import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Row, Col, Dropdown, Spinner } from "react-bootstrap";
import { Doughnut } from "react-chartjs-2";
import { BASE_URL } from "../constants.js";
import moment from "moment";

function RoomDetails({ show, onHide, room }) {
  const [accommodation, setAccommodation] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = moment();
  const formattedDate = today.format("DD MMMM YYYY");

  useEffect(() => {
    if (show && room) {
      setLoading(true);
      fetch(`${BASE_URL}/accommodation/${room._id}`)
        .then((res) => res.json())
        .then((data) => {
          setAccommodation(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching data:", err);
          setLoading(false);
        });
    }
  }, [show, room]);

  // Delay clearing data to avoid modal flicker
  useEffect(() => {
    if (!show) {
      const timeout = setTimeout(() => setAccommodation(null), 300); // Match modal close animation
      return () => clearTimeout(timeout);
    }
  }, [show]);

  const occupiedPlaces = accommodation?.occupant.length || 0;
  const totalCapacity = accommodation?.capacity || 1;
  const occupancyPercentage = Math.round((occupiedPlaces / totalCapacity) * 100);
  const freePlaces = totalCapacity - occupiedPlaces;
  const freePercentage = Math.round((freePlaces / totalCapacity) * 100);

  const chartData = {
    labels: ["Occupied", "Free"],
    datasets: [
      {
        data: [occupiedPlaces, freePlaces],
        backgroundColor: ["#003f5c", "#E0E0E0"],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    cutout: "80%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const label = chartData.labels[tooltipItem.dataIndex];
            const value = chartData.datasets[0].data[tooltipItem.dataIndex];
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
  };

  const centerTextPlugin = {
    id: "centerText",
    beforeDraw: (chart) => {
      const { width, height } = chart;
      const ctx = chart.ctx;
      ctx.save();
      ctx.font = "bold 20px Arial";
      ctx.fillStyle = "#003f5c";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${occupancyPercentage}%`, width / 2, height / 2);
      ctx.restore();
    },
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Occupancy Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Card className="p-3 position-relative" style={{ borderRadius: "12px", minHeight: 320 }}>
          {/* Spinner Overlay */}
          {loading && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                borderRadius: "12px",
                zIndex: 10,
              }}
            >
              <Spinner animation="border" />
            </div>
          )}

          <Card.Body style={{ opacity: loading ? 0.4 : 1, transition: "opacity 0.3s ease" }}>
            {accommodation && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">{room?.name}</h5>
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" size="sm">Today</Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item>Today</Dropdown.Item>
                      <Dropdown.Item>{formattedDate}</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <Row>
                  <Col xs={6}>
                    <h3 className="mb-0">{occupancyPercentage}%</h3>
                    <small className="text-muted">Occupied</small>
                  </Col>
                  <Col xs={6} className="text-end">
                    <h3 className="mb-0">{freePlaces}/{totalCapacity}</h3>
                    <small className="text-muted">Free Places</small>
                  </Col>
                </Row>

                <div className="my-3 text-center" style={{ width: "160px", margin: "0 auto" }}>
                  <Doughnut data={chartData} options={chartOptions} plugins={[centerTextPlugin]} />
                </div>

                <Row className="mt-3">
                  <Col xs={6} className="text-center">
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        backgroundColor: "#003f5c",
                        marginRight: 5,
                        borderRadius: 2,
                      }}
                    />
                    <small>Occupied</small>
                  </Col>
                  <Col xs={6} className="text-center">
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        backgroundColor: "#E0E0E0",
                        marginRight: 5,
                        borderRadius: 2,
                      }}
                    />
                    <small>Free</small>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RoomDetails;
